import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import RequestsService from 'services/RequestService';
import { RequestModel } from 'services/EpdClient';
import EmptyRequestsList from './EmptyRequestsList';
import { toaster } from 'components/v2/toast';
import RequestItem from './RequestItem';
import { confirmAlert } from 'components/v2/confirm-dialog/confirmAlert';
import { useMutation } from '@tanstack/react-query';
import { ConfirmDialog } from 'primereact/confirmdialog';
import LoadingSkeleton from '../LoadingSkeleton';
import { HyperLink } from 'styles/Styles.styled';
import { EpdLinks } from 'routes/EpdRoutes';

const UserSettingsRequestsTab = () => {
  const [requests, setRequests] = useState<RequestModel[] | null>(null);
  const fetchRequests = async () => {
    try {
      const result = await RequestsService.getUserRequests();
      setRequests(result);
    } catch {
      toaster({
        severity: 'error',
        summary: 'Something went wrong.',
        details: 'Please try again later or contact support.',
      });
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const declineMutation = useMutation({
    mutationFn: (requestId: string) => RequestsService.rejectRequest(requestId),
    onSuccess: async () => {
      await fetchRequests();
      toaster({ severity: 'success', summary: 'Operation completed successfully!' });
    },
    onError: () => {
      toaster({
        severity: 'error',
        summary: 'Something went wrong.',
        details: 'Please try again later or contact support.',
      });
    },
  });

  const handleRemoveRequest = (requestId: string, companyName?: string) => {
    confirmAlert({
      caption: `Do you want to remove the access request to ${companyName}?`,
      description: <div>Your request will be removed.</div>,
      acceptLabel: 'Confirm',
      rejectLabel: 'Cancel',
      onConfirm: () => declineMutation.mutate(requestId),
    });
  };

  const isLoading = declineMutation.isPending;

  if (requests === null || isLoading) return <LoadingSkeleton />;

  if (requests.length === 0) return <EmptyRequestsList />;

  return (
    <>
      <ConfirmDialog />
      <InfoBlock>
        Here you can find the organisations you have{' '}
        {<HyperLink href={EpdLinks.organisation('connect')}>requested access</HyperLink>} to.
      </InfoBlock>
      <RequestsList>
        {requests?.map((request) => (
          <RequestItem
            key={request.id}
            id={request.id}
            companyName={request.companyName}
            createdAt={request.createdAt}
            onRemoveRequest={handleRemoveRequest}
          />
        ))}
      </RequestsList>
    </>
  );
};

const RequestsList = styled.div`
  margin-top: 16px;
`;

const InfoBlock = styled.div`
  width: 100%;
  height: 52px;
  background-color: #b4d0b64d;
  border-radius: 4px;
  padding: 16px 0 16px 16px;
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
`;

export default UserSettingsRequestsTab;
